import { OktaMetaData } from 'module/api/account/types';
import { CognitoAPI } from 'module/authentication/api/cognito';
import { LoginWidgetLinks } from 'module/authentication/types';
import { Checkbox } from 'module/ui/components/atoms/checkbox';
import { Form } from 'module/ui/components/atoms/form';
import {
  BackgroundColor,
  Input,
  InputBorder,
  InputFocus,
} from 'module/ui/components/atoms/input/input';
import { Short01, Text01 } from 'module/ui/components/styles';
import { Color } from 'module/ui/components/styles/colors';
import {
  ViewFilled,
  ViewOffFilled,
  WarningAltFilled,
} from '@carbon/icons-react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate, useLocation } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { Col, Row } from 'antd';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ForgotPasswordLink } from '../forgot-password-link';
import { useCognitoAuth } from './use-cognito-auth';
import { SsoSamlButton } from '../saml-button/button';

enum PasswordInputType {
  PASSWORD = 'password',
  TEXT = 'text',
}

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  & button {
    height: 50px;
  }
  margin-bottom: var(--spacing-05);
`;

const WidgetContainer = styled.div`
  width: 400px;
  display: flex;
  flex-direction: column;
  position: relative;

  .ant-form-item {
    margin-bottom: 0 !important;
  }

  .ant-form-item:nth-child(1) {
    padding-bottom: var(--spacing-03) !important;
  }

  .ant-form-item-control-input-content input {
    width: 100%;
    padding: 0 var(--spacing-05);
    ${InputBorder};
    ${Short01};
    ${Text01};
    ${BackgroundColor};
    height: var(--input-height-lg);

    &.focused-input {
      ${InputFocus};
    }
  }
`;

const SignInButton = styled(Button)`
  text-align: left;
  height: var(--input-height-lg);
  margin-bottom: var(--spacing-03);
`;

const Extras = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-07);
`;

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px !important;
  font-weight: 400 !important;
  font-family: var(--font-family) !important;
`;

const AlertWrapper = styled.div<{ color: Color }>`
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--spacing-04);
  margin: var(--spacing-05) 0;
  background-color: var(--red-10);
  border-left: 4px solid var(--red-50);
  color: var(--red-60);

  svg {
    margin-right: var(--spacing-03);
    fill: var(--red-50);
  }
`;

const PasswordContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ShowPasswordContainer = styled.div`
  cursor: pointer;
  position: absolute;
  right: var(--spacing-05);
  color: var(--gray-30);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const errorMessages = [
  'UserMigration failed with error User account already exists.',
  'UserMigration failed with error User migrated successfully.',
];

const lambdaErrorMessages = [
  'UserMigration failed with error Incorrect username or password.',
];

interface Props {
  oktaMetaData?: OktaMetaData;
  isSamlMandatory: boolean;
  links: LoginWidgetLinks;
}

export const CognitoSignInWidget = ({
  oktaMetaData,
  isSamlMandatory,
  links,
}: Props) => {
  const [form] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [notAnErrorBuErrorMessage, setNotAnErrorBuErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordInputType, setPasswordInputType] = useState(
    PasswordInputType.PASSWORD,
  );

  const isCustomAuthentication = oktaMetaData?.customAuthentication;

  const [showPassword, setShowPassword] = useState(false);
  const isSamlEnabled = useMemo(
    () => !!(oktaMetaData?.idps ?? []).filter((idp) => idp.enabled).length,
    [oktaMetaData?.idps],
  );

  const onClick = () => {
    setErrorMessage('');
    form.validateFields().then((values) => {
      const { email, password } = values.information[0];

      if (!email || !password) {
        setLoading(false);

        return;
      }

      setLoading(true);

      CognitoAPI.loginWithCognito(
        {
          userName: email,
          password,
          clientId: oktaMetaData?.clientId,
          userNamesuffix: oktaMetaData?.userName,
        },
        isCustomAuthentication,
      )
        .then((res: any) => {
          setLoading(false);
          const { token } = res;

          navigate(`${location.pathname}?token=${token}&cognito=true`);
        })
        .catch((error) => {
          if (errorMessages.includes(error?.message)) {
            setNotAnErrorBuErrorMessage('Something went wrong, try again');
          } else if (lambdaErrorMessages.includes(error?.message)) {
            setErrorMessage('Incorrect username or password.');
          } else if (error?.response?.data?.errors[0]?.errorMessage) {
            setErrorMessage(error.response.data.errors[0].errorMessage);
          } else if (error?.message) {
            setErrorMessage(error.message);
          }
          setLoading(false);
        });
    });
  };

  const { samlFlowHandler, googleSSOHandler } = useCognitoAuth({
    oktaMetaData: oktaMetaData,
  });
  const { oktaAuth } = useOktaAuth();
  const params = new URLSearchParams(location.search);
  const partnerParam = params.get('partner');

  return (
    <WidgetContainer>
      {isSamlMandatory && isSamlEnabled && !partnerParam ? (
        <ButtonContainer>
          {(oktaMetaData?.idps ?? [])
            .filter((idp) => idp.enabled)
            .map((idp) => {
              return (
                <SsoSamlButton
                  key={idp.idpId}
                  idp={idp}
                  googleSSOHandler={googleSSOHandler}
                  samlFlowHandler={samlFlowHandler}
                  isCognitoEnabled
                  oktaAuth={oktaAuth}
                />
              );
            })}
        </ButtonContainer>
      ) : (
        <>
          {errorMessage && (
            <AlertWrapper color="red">
              <WarningAltFilled size={20} />
              {errorMessage}
            </AlertWrapper>
          )}

          {notAnErrorBuErrorMessage && (
            <AlertWrapper color="blue">
              <WarningAltFilled size={20} />
              {notAnErrorBuErrorMessage}
            </AlertWrapper>
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={{ information: [{ email: '', password: '' }] }}
            onKeyDownCapture={(e) => {
              if (e.key === 'Enter' && !loading) {
                form.validateFields().then(() => {
                  const { email, password } =
                    form.getFieldValue('information')[0];

                  if (email && password) {
                    e.preventDefault();
                    onClick();
                  }
                });
              }
            }}
          >
            <Form.List name="information">
              {(fields) => {
                return (
                  <>
                    {fields.map((field) => (
                      <Row key={field.key}>
                        <Col span={24}>
                          <Form.Item
                            id="email"
                            {...field}
                            name={[field.name, 'email']}
                            fieldKey={[field.fieldKey ?? '', 'email']}
                            label={<FormattedMessage id="email" />}
                            rules={[
                              {
                                required: true,
                                message: (
                                  <FormattedMessage id="please_enter_email" />
                                ),
                              },
                            ]}
                          >
                            <Input placeholder="email" type="email" />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            id="password"
                            {...field}
                            name={[field.name, 'password']}
                            fieldKey={[field.fieldKey ?? '', 'password']}
                            label={<FormattedMessage id="password" />}
                            rules={[
                              {
                                required: true,
                                message: (
                                  <FormattedMessage id="please_enter_password" />
                                ),
                              },
                            ]}
                          >
                            <PasswordContainer>
                              <Input
                                placeholder="password"
                                type={passwordInputType}
                              />
                              <ShowPasswordContainer
                                onClick={() => {
                                  setShowPassword(!showPassword);
                                  setPasswordInputType(
                                    showPassword
                                      ? PasswordInputType.PASSWORD
                                      : PasswordInputType.TEXT,
                                  );
                                }}
                              >
                                {!showPassword ? (
                                  <ViewFilled size={16} />
                                ) : (
                                  <ViewOffFilled size={16} />
                                )}
                              </ShowPasswordContainer>
                            </PasswordContainer>
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                  </>
                );
              }}
            </Form.List>
          </Form>
          <Extras>
            <StyledCheckbox
              checked={checked}
              onChange={(event) => {
                setChecked(event.target.checked);
              }}
            >
              <FormattedMessage id="remember_me" />
            </StyledCheckbox>
            <ForgotPasswordLink
              isSamlMandate={isSamlMandatory}
              forgotPasswordLink={links.forgotPasswordUrl}
            />
          </Extras>
          <SignInButton loading={loading} variant="primary" onClick={onClick}>
            <FormattedMessage id="magic_link.otp.sigin.button.text" />
          </SignInButton>
        </>
      )}
    </WidgetContainer>
  );
};
